// Pure availability logic + rate-limiter for the faceted ChiDesk spa flow.
//
// The panel fetches availability ON-SELECT and LIVE (see useChideskAvailability.ts):
// for a selected treatment it fires one available_times call per bookable
// therapist and MERGES the results into a per-treatment union. This module holds
// the pure pieces of that flow — therapist/time-of-day filters, the sequential
// multi-treatment chain resolver, the feasible-start derivation, and the shared
// rate limiter. It holds NO React and NO I/O beyond the task the limiter runs, so
// it stays deterministic and unit-testable.
//
// TZ MODEL (corrected 2026-07-30 — the old "fake-Z is local wall-clock"
// doctrine was WRONG): ChiDesk's API speaks REAL UTC. Slot `Start` strings are
// genuine UTC instants; ChiDesk's own calendar renders them in the site's local
// timezone. Proof: we sent "2026-08-05 16:30", ChiDesk's calendar showed 18:30
// SAST; therapist availability read 06:00→16:45 "API time" = 08:00→18:45 SAST,
// exactly their real roster. RULES:
//   • BOOKING: echo the slot's UTC `start` string VERBATIM — the instant is
//     already correct. Never re-derive it from a displayed label.
//   • DISPLAY + SEMANTICS (labels, minute-of-day, yield grid, open-hours,
//     time-of-day filter): convert UTC minutes to TENANT-LOCAL minutes by
//     adding the tenant's UTC offset (tzOffsetMinutes below). buildDaySlots is
//     the choke point — everything downstream is tenant-local.
//   • Duration/cursor arithmetic is offset-invariant (deltas cancel), so
//     resolveChain/turnaround math works unchanged in local-minute space, and
//     shiftIsoWallClock keeps operating on the UTC strings (instant math).

import type { WidgetChideskTreatment } from "../types";
import type { ChideskDaySlots, ChideskSlot } from "./chidesk";

// ─── Time-of-day filter ─────────────────────────────────────────────────────
export type TimeOfDay = "any" | "morning" | "afternoon" | "evening";

// ─── Regex wall-clock helpers (NEVER Date on ChiDesk strings) ────────────────

/** Wall-clock minute-of-day from a ChiDesk start string (fake-Z or "YYYY-MM-DD HH:MM"). */
export function wallMinutesOf(start: string): number {
  const m = /[T\s](\d{1,2}):(\d{2})/.exec(start);
  if (!m) return 0;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(min) ? min : 0);
}

/**
 * The tenant timezone's UTC offset in minutes (SAST → 120). Computed via Intl
 * so DST-observing zones stay correct; falls back to +120 (Africa/Johannesburg,
 * no DST) if the zone string is unknown. Used to convert ChiDesk's REAL-UTC
 * slot times into tenant-local display minutes.
 */
export function tzOffsetMinutes(timeZone: string, at: Date = new Date()): number {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    });
    const p: Record<string, string> = {};
    for (const part of dtf.formatToParts(at)) p[part.type] = part.value;
    const asUtc = Date.UTC(
      Number(p.year), Number(p.month) - 1, Number(p.day),
      Number(p.hour === "24" ? "0" : p.hour), Number(p.minute), Number(p.second),
    );
    return Math.round((asUtc - at.getTime()) / 60_000);
  } catch {
    return 120;
  }
}

/** Normalize a possibly-out-of-range minute into [0, 1440). */
export function normMinute(min: number): number {
  return ((Math.round(min) % 1440) + 1440) % 1440;
}

/** Minute-of-day from a human label like "09:00 AM" / "14:30" (null if unparseable). */
export function parseClockLabel(label: string): number | null {
  const m = /(\d{1,2}):(\d{2})\s*([AP]M)?/i.exec(label);
  if (!m) return null;
  let h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  const mer = m[3]?.toUpperCase();
  // ChiDesk labels afternoon slots as a 24-HOUR hour WITH a PM suffix, e.g.
  // "14:00 PM" / "15:15 PM". Only shift a genuine 12-hour PM hour (1–11) by 12 —
  // an already-24-hour hour (>=13) must be left as-is, or "14:00 PM" becomes
  // 26:00 (minute 1560) and never matches a yield-grid time, silently dropping
  // every afternoon slot once the grid filter is on.
  if (mer === "AM") {
    if (h === 12) h = 0;
  } else if (mer === "PM") {
    if (h < 12) h += 12;
  }
  return h * 60 + min;
}

/** "09:00 AM" from a minute-of-day (wraps to a 24h day). */
export function labelFromWallMinutes(min: number): string {
  const total = ((Math.round(min) % 1440) + 1440) % 1440;
  const h24 = Math.floor(total / 60);
  const m = total % 60;
  const mer = h24 < 12 ? "AM" : "PM";
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${mer}`;
}

/**
 * Shift a ChiDesk start string by `deltaMin`, textually — preserving the fake-Z
 * wall clock. Date.UTC is used ONLY for calendar-day rollover; the returned
 * string round-trips through the proxy's getUTC* → wall-clock mapping.
 */
export function shiftIsoWallClock(anchorIso: string, deltaMin: number): string {
  const m = /(\d{4})-(\d{2})-(\d{2})[T\s](\d{1,2}):(\d{2})/.exec(anchorIso);
  if (!m) return anchorIso;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const baseMin = Number(m[4]) * 60 + Number(m[5]);
  const total = baseMin + deltaMin;
  const carry = Math.floor(total / 1440);
  const dayMin = ((total % 1440) + 1440) % 1440;
  const rolled = new Date(Date.UTC(y, mo - 1, d + carry));
  const pad = (n: number) => String(n).padStart(2, "0");
  const yy = rolled.getUTCFullYear();
  const mm = pad(rolled.getUTCMonth() + 1);
  const dd = pad(rolled.getUTCDate());
  const hh = pad(Math.floor(dayMin / 60));
  const mi = pad(dayMin % 60);
  return `${yy}-${mm}-${dd}T${hh}:${mi}:00Z`;
}

// ─── Pure filters ────────────────────────────────────────────────────────────

export function matchesTherapist(slot: ChideskSlot, guid: string | null): boolean {
  return guid == null || slot.employeeIds.includes(guid);
}

export function matchesTod(min: number, f: TimeOfDay): boolean {
  return (
    f === "any" ||
    (f === "morning" && min < 720) ||
    (f === "afternoon" && min >= 720 && min < 1020) ||
    (f === "evening" && min >= 1020)
  );
}

/**
 * Resolve which therapist GUID to book for a slot: an explicit filter pick when
 * that therapist is free, else the slot's first free employee, else null (let
 * ChiDesk assign — matches the historical employees:[] path).
 */
export function pickTherapistGuid(slot: ChideskSlot, filterGuid: string | null): string | null {
  if (filterGuid && slot.employeeIds.includes(filterGuid)) return filterGuid;
  return slot.employeeIds[0] ?? null;
}

// ─── Per-treatment status ────────────────────────────────────────────────────

// idle    = not requested yet (no decoration)
// loading = in flight
// error   = ok:false (FAIL OPEN — never greyed, still bookable)
// none    = ok:true + slots:[] (DEFINITIVELY none for the day → grey the card)
// ok      = has slots
export type CardStatus = "idle" | "loading" | "error" | "none" | "ok";

/** 5-state status straight from a slotsFor() result (idle = not requested). */
export function statusOf(ds: ChideskDaySlots | "loading" | undefined): CardStatus {
  if (ds === undefined) return "idle";
  if (ds === "loading") return "loading";
  if (!ds.ok) return "error";
  return ds.slots.length === 0 ? "none" : "ok";
}

export type SlotsForFn = (rowId: string) => ChideskDaySlots | "loading" | undefined;

// ─── Chain resolution (multi-treatment, sequential) ──────────────────────────

export interface ChainLeg {
  treatment: WidgetChideskTreatment;
  start: string; // EXACT ChiDesk fake-Z Start — echoed verbatim on booking
  staffGuid: string | null;
  /**
   * Every therapist GUID free at this exact slot (the union's employeeIds). For
   * an "Any available" booking these are the CANDIDATES the server picks a fair
   * (least-loaded) therapist from — so bookings circulate instead of always
   * landing on the first. For a specific pick it's just that therapist.
   */
  employeeIds: string[];
  durationMin: number;
  minuteOfDay: number;
  label: string;
}

/**
 * Resolve a whole ordered chain starting from `firstSlot`. Each later leg snaps
 * to the cursor minute (exact byMinute hit, else the first slot >= cursor with a
 * therapist match — handles non-15-min durations). An errored leg FAILS OPEN
 * with a synthesized unverified slot at the cursor. Returns null when any leg
 * has no slot >= cursor (that start never becomes a chip / is blocked on add).
 */
export function resolveChain(
  chainOrdered: WidgetChideskTreatment[],
  slotsFor: SlotsForFn,
  firstSlot: ChideskSlot,
  filterGuid: string | null,
  turnaround = 0,
): ChainLeg[] | null {
  const legs: ChainLeg[] = [];
  let cursor = firstSlot.minuteOfDay;
  for (let i = 0; i < chainOrdered.length; i++) {
    const t = chainOrdered[i]!;
    const dur = t.duration_minutes ?? 60;
    let slot: ChideskSlot | undefined;
    if (i === 0) {
      slot = firstSlot;
    } else {
      const ds = slotsFor(t.id);
      if (!ds || ds === "loading") return null;
      if (!ds.ok) {
        // errored leg → FAIL OPEN: synthesize an unverified slot at the cursor.
        slot = {
          start: shiftIsoWallClock(firstSlot.start, cursor - firstSlot.minuteOfDay),
          label: labelFromWallMinutes(cursor),
          minuteOfDay: cursor,
          employeeIds: filterGuid ? [filterGuid] : [],
        };
      } else {
        const exact = ds.byMinute.get(cursor);
        slot =
          exact && matchesTherapist(exact, filterGuid)
            ? exact
            : ds.slots.find((s) => s.minuteOfDay >= cursor && matchesTherapist(s, filterGuid));
        if (!slot) return null;
      }
    }
    legs.push({
      treatment: t,
      start: slot.start,
      staffGuid: pickTherapistGuid(slot, filterGuid),
      // Specific pick → only that therapist is a candidate; "any" → all free.
      employeeIds: filterGuid
        ? slot.employeeIds.filter((g) => g === filterGuid)
        : [...slot.employeeIds],
      durationMin: dur,
      minuteOfDay: slot.minuteOfDay,
      label: slot.label,
    });
    // Leave the turnaround gap before the next leg (back-to-back chain legs).
    cursor = slot.minuteOfDay + dur + turnaround;
  }
  return legs;
}

// ─── Feasible start chips (primary flow) ─────────────────────────────────────

export interface FeasibleChip {
  start: string;
  label: string;
  minuteOfDay: number;
}
export type FeasibleResult =
  | { status: "loading" }
  | { status: "error-anchor" }
  | { status: "ready"; chips: FeasibleChip[] };

/**
 * The bookable start times for a chain: anchor slots that pass therapist + tod
 * and for which the whole chain resolves. Single-treatment collapses to
 * "anchor slots filtered by therapist+tod". An errored anchor returns
 * 'error-anchor' (offer Retry + manual-time) — never a false "unavailable".
 */
export function deriveFeasibleStarts(
  chain: WidgetChideskTreatment[],
  slotsFor: SlotsForFn,
  opts: {
    filterGuid: string | null;
    tod: TimeOfDay;
    /**
     * The venue's OPEN windows for the chosen day, as local wall-clock
     * minute-of-day ranges. ChiDesk's availableTimes ignores the spa's opening
     * hours (it returns every slot a therapist isn't booked, e.g. from 06:00),
     * so we cap the chips to these. minuteOfDay is the fake-Z wall clock =
     * local, and opens_at/closes_at are local too → compared directly, no tz
     * math. Empty/undefined → no cap (fail open, e.g. no hours configured).
     */
    openRanges?: ReadonlyArray<{ start: number; end: number }>;
    /**
     * For TODAY only: the current wall-clock minute-of-day. Slots that START
     * before this are dropped so a guest can't book a time that has already
     * passed. Same local wall-clock basis as minuteOfDay (no tz math).
     * Undefined on a future day → no cutoff.
     */
    nowMinute?: number;
    /**
     * Yield management: the tenant's fixed allowed start times as minute-of-day
     * (e.g. [540, 615, 690…] for 09:00, 10:15, 11:30…). When set, ONLY these
     * starts are exposed — each still gated by real ChiDesk availability + the
     * checks above. Empty/undefined → offer every available time.
     */
    bookingGrid?: ReadonlyArray<number>;
    /**
     * Cleanup/turnaround minutes after each treatment: a start is kept only if
     * treatment + this gap finishes before close, and back-to-back chain legs
     * leave this gap. 0/undefined → no gap.
     */
    turnaround?: number;
  },
): FeasibleResult {
  if (chain.length === 0) return { status: "ready", chips: [] };
  const anchor = chain[0]!;
  const anchorDur = anchor.duration_minutes ?? 60;
  const turnaround = opts.turnaround ?? 0;
  const grid = opts.bookingGrid && opts.bookingGrid.length > 0
    ? opts.bookingGrid
    : null;
  const a = slotsFor(anchor.id);
  if (a === undefined || a === "loading") return { status: "loading" };
  if (!a.ok) return { status: "error-anchor" };
  // Wait for EVERY selected treatment's slots before deciding "ready + empty".
  // The anchor may resolve first (limiter dispatches ~140ms apart), and
  // resolveChain drops every anchor slot while a later leg is still loading —
  // so gating on the anchor alone would paint a false "No availability, try
  // next day" flash. Errored legs must NOT gate here: resolveChain fails them
  // open with a synthesized cursor slot, so only not-yet-resolved legs block.
  for (let i = 1; i < chain.length; i++) {
    const ds = slotsFor(chain[i]!.id);
    if (ds === undefined || ds === "loading") return { status: "loading" };
  }
  const seen = new Set<string>();
  const chips: FeasibleChip[] = [];
  for (const s of a.slots) {
    if (!matchesTherapist(s, opts.filterGuid)) continue;
    if (!matchesTod(s.minuteOfDay, opts.tod)) continue;
    // Never offer a start that has already passed today.
    if (opts.nowMinute != null && s.minuteOfDay < opts.nowMinute) continue;
    // Yield grid: expose ONLY the tenant's fixed start times (still gated by the
    // real ChiDesk availability of `a.slots` + the checks here). No grid → all.
    if (grid && !grid.includes(s.minuteOfDay)) continue;
    // Cap to the venue's opening hours: the treatment must START at/after open
    // and FINISH by close. Turnaround/cleanup is deliberately NOT required to
    // fit before close — it runs AFTER the last treatment of the day (staff
    // clean up past closing), so counting it here wrongly hid the last feasible
    // afternoon start(s). The turnaround gap BETWEEN bookings is still enforced
    // (resolveChain's cursor for chained treatments + the server-side
    // localTherapistConflict for other bookings). Only applied when hours are
    // configured (fail open).
    if (
      opts.openRanges &&
      opts.openRanges.length > 0 &&
      !opts.openRanges.some(
        (r) => s.minuteOfDay >= r.start && s.minuteOfDay + anchorDur <= r.end,
      )
    ) {
      continue;
    }
    if (seen.has(s.start)) continue;
    if (resolveChain(chain, slotsFor, s, opts.filterGuid, turnaround) === null) {
      continue;
    }
    seen.add(s.start);
    chips.push({ start: s.start, label: s.label, minuteOfDay: s.minuteOfDay });
  }
  chips.sort((x, y) => x.minuteOfDay - y.minuteOfDay);
  return { status: "ready", chips };
}

// ─── Shared rate limiter (fronts EVERY availableTimes call) ──────────────────
//
// ChiDesk caps at 8 req/sec. A single limiter (created once per hook) with
// maxInFlight 4 + minGap 140ms dispatches at most ~7.1/sec regardless of
// maxInFlight, so an on-select union — one call per bookable therapist, fired at
// once — stays comfortably under the cap while still resolving progressively.

export const CANCELLED = Symbol("chidesk-cancelled");
export type Cancelled = typeof CANCELLED;

interface Job {
  task: () => Promise<unknown>;
  isCancelled?: () => boolean;
  resolve: (v: unknown) => void;
  reject: (e: unknown) => void;
}

export interface ChideskLimiter {
  run<T>(task: () => Promise<T>, isCancelled?: () => boolean): Promise<T | Cancelled>;
  clear(): void;
  /** Synchronously resolve (→ CANCELLED) any still-QUEUED job flagged cancelled,
   *  so its key stops being counted in-flight and can be re-requested. */
  sweepCancelled(): void;
}

export function createChideskLimiter(opts?: {
  maxInFlight?: number;
  minGapMs?: number;
}): ChideskLimiter {
  const maxInFlight = opts?.maxInFlight ?? 4;
  const minGapMs = opts?.minGapMs ?? 140;
  let inFlight = 0;
  let lastDispatch = 0;
  let pending: Job[] = [];
  let pumpTimer: ReturnType<typeof setTimeout> | null = null;

  function pump(): void {
    if (pumpTimer != null) {
      clearTimeout(pumpTimer);
      pumpTimer = null;
    }
    while (pending.length > 0 && inFlight < maxInFlight) {
      // Bail on a stale scheduling decision (day change / mode exit) BEFORE the
      // minGap gate — a cancelled browse backlog must drain instantly instead
      // of each job burning a 140ms dispatch slot ahead of the primary flow.
      if (pending[0]!.isCancelled?.()) {
        pending.shift()!.resolve(CANCELLED);
        continue;
      }
      const wait = minGapMs - (Date.now() - lastDispatch);
      if (wait > 0) {
        pumpTimer = setTimeout(pump, wait);
        return;
      }
      const job = pending.shift()!;
      lastDispatch = Date.now();
      inFlight += 1;
      job.task().then(
        (v) => {
          inFlight -= 1;
          job.resolve(v);
          pump();
        },
        (e) => {
          inFlight -= 1;
          job.reject(e);
          pump();
        },
      );
    }
  }

  return {
    run<T>(task: () => Promise<T>, isCancelled?: () => boolean): Promise<T | Cancelled> {
      return new Promise<T | Cancelled>((resolve, reject) => {
        pending.push({
          task: task as () => Promise<unknown>,
          isCancelled,
          resolve: resolve as (v: unknown) => void,
          reject,
        });
        pump();
      });
    },
    clear(): void {
      const dropped = pending;
      pending = [];
      for (const job of dropped) job.resolve(CANCELLED);
    },
    sweepCancelled(): void {
      // When a generation is superseded (day / therapist / selection change) its
      // still-QUEUED jobs would otherwise sit here until dropped CANCELLED much
      // later. Drain them now: resolve every still-pending cancelled job so the
      // live-union hook's callbacks see CANCELLED promptly and stop pacing behind
      // them under the rate limit.
      const keep: Job[] = [];
      for (const j of pending) {
        if (j.isCancelled?.()) j.resolve(CANCELLED);
        else keep.push(j);
      }
      pending = keep;
    },
  };
}
